import React from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { RootState } from '../../store';
import { TaskBoard } from '../../features/task-board';
import { TaskApi } from '../../features/task-board/api/taskApi';
import { TaskUser } from '../../features/task-board/types';

const CEO_USER_TYPE_IDS = [1, 2];
const AI_CHAT_ALLOWED = ['shashwat', 'keshav', 'mumbai'];

const AVATAR_COLORS = [
  'bg-purple-600', 'bg-blue-600', 'bg-emerald-600',
  'bg-rose-600', 'bg-amber-600', 'bg-cyan-600', 'bg-indigo-600',
];

function toInitials(name: string): string {
  return name.split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

function toTaskUser(member: { id: number; name: string }, index: number): TaskUser {
  return {
    id: member.id,
    name: member.name,
    initials: toInitials(member.name),
    avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
  };
}

export const TaskBoardPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const { data: members = [] } = useQuery({
    queryKey: ['task-members'],
    queryFn: TaskApi.getMembers,
    staleTime: 5 * 60 * 1000,
  });

  const apiUsers: TaskUser[] = members.map((m, i) => toTaskUser(m, i));

  // Use the real auth user ID so task fetch and task create use the same ID
  const realId = Number(user?.id ?? 0);
  const matched = apiUsers.find(u => u.id === realId);
  const currentUser: TaskUser = matched ?? {
    id: realId || 1,
    name: user?.name ?? 'Unknown',
    initials: toInitials(user?.name ?? 'U'),
    avatarColor: AVATAR_COLORS[realId % AVATAR_COLORS.length],
  };

  // Always include currentUser in the member list so they can be assigned tasks
  const allUsers: TaskUser[] = matched
    ? apiUsers
    : apiUsers.length > 0
      ? [currentUser, ...apiUsers]
      : [currentUser];

  const isAdminOrCeo = CEO_USER_TYPE_IDS.includes(user?.userTypeId ?? -1);
  const canUseAIChat = AI_CHAT_ALLOWED.some(name =>
    (user?.name ?? '').toLowerCase().includes(name)
  );

  return (
    <div className='h-full flex flex-col'>
      <TaskBoard
        currentUser={currentUser}
        isAdminOrCeo={isAdminOrCeo}
        canUseAIChat={canUseAIChat}
        allUsers={allUsers}
      />
    </div>
  );
};
